import {
  CoreDoms,
  LayoutContext,
  ViewMode,
  Viewers,
  EditorExperienceType,
} from '@taopix/taopix-core';
import {PageInterface} from '@taopix/taopix-core/dist/types/types';
import {
  Button,
  GridViewIcon,
  ListViewIcon,
  LoadingOverlay,
  MatchesMediaQuery,
  Theme,
  ThemeName,
} from '@taopix/taopix-design-system';
import classNames from 'classnames';
import React, {useCallback, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {LayoutSelector, PagesSelector} from '../../../Selectors';
import {UserInterfaceActions} from '../../Context/UserInterface/UserInterfaceActionTypes';
import {useUiState} from '../../Context/UserInterface/UserInterfaceContext';
import Preview from '../../Preview/Preview';
import ZoomControl from '../../ZoomControl/ZoomControl';
import {GridSizes} from '../../GridSizeSelector/GridSizeSelector';
import {useTaopix} from '../../Context/TaopixContext';
import {useExperience} from '../../Experience/ExperienceProvider';
import PageName from '../../Page/PageName';

const SharePreview = (): JSX.Element => {
  const ui = useUiState();
  const app = useTaopix();
  const {experience} = useExperience();
  const [viewMode, setViewMode] = useState(ViewMode.List);

  useEffect(() => {
    setViewMode(experience?.EDITOR?.workArea?.defaultView === 'gv' ? ViewMode.Grid : ViewMode.List);
  }, [experience]);

  const smallScreen = MatchesMediaQuery('(max-width: 799px)');

  useEffect(() => {
    if (smallScreen) {
      ui.dispatch(UserInterfaceActions.setIsSmallScreen(true));
    } else {
      ui.dispatch(UserInterfaceActions.setIsSmallScreen(false));
    }
  }, [smallScreen]);

  // get some props from the core
  const pages = useSelector(PagesSelector);

  // get the layout from the store
  const layout = useSelector(LayoutSelector);

  const containerRef = React.useRef<HTMLDivElement>(null);

  const onSetViewMode = useCallback((newMode: ViewMode) => {
    setViewMode(newMode);
  }, []);

  const appClasses = classNames(
    'flex',
    'flex-col',
    'flex-1',
    'max-w-full',
    'max-h-full',
    'min-h-0'
  );

  const viewerToolbarClasses = classNames(
    'absolute',
    'top-0',
    'left-0',
    'px-sm',
    'flex',
    'z-10',
    'w-full',
    'justify-center',
    'overflow-hidden',
    'lg:h-[50rem]',
    'sm:h-[60rem]',
    'transition-opacity',
    'pointer-events-none' // Allow pointer events to be picked up by the canvasses
  );

  const centerClasses = classNames(
    'absolute',
    'flex',
    'mt-sm',
    'space-x-xs',
    'pointer-events-auto'
  );

  const rightSideClasses = classNames(
    'absolute',
    'right-sm',
    'flex',
    'mt-sm',
    'space-x-xs',
    'pointer-events-auto'
  );

  const gridSizeToolbarClasses = classNames(
    'flex',
    'shadow-md',
    'rounded-themeCornerSize',
    'transition-[transform]',
    'ease-[cubic-bezier(0.42,0,0,2.27)]'
  );

  const workAreaClassNames = classNames(
    'flex',
    'flex-col',
    'flex-1',
    'relative',
    'min-h-0',
    'items-center',
    'overflow-hidden'
  );

  const paginationWrapperClasses = classNames(
    'relative',
    'flex',
    'w-full',
    'items-center',
    'flex-1',
    'overflow-hidden' // prevents this container getting bigger when the project viewer is zoomed
  );

  const projectViewerClassNames = classNames(
    'flex',
    'items-start',
    'relative',
    'w-full',
    'h-full',
    'transition-[all] duration-[0.3s] ease-out',
    ui.state.zoomLevel === 100 && 'justify-center',
    'snap-mandatory',

    // List and Preview views
    [ViewMode.List, ViewMode.Preview].includes(viewMode) && ui.state.zoomLevel <= 120 && 'snap-y',

    // Single page view
    ViewMode.Single === viewMode && [
      'flex-col',
      'transition-[padding]',
      '[.focus-mode-off_&]:pt-[60rem]',
      ui.state.zoomLevel === 100 && 'snap-x',
    ]
  );

  const pagesContainerClasses = classNames(
    viewMode === ViewMode.List && [
      pages.length > 1
        ? 'lg:mt-[100px] sm:mt-[40px] after:h-[60px] after:w-full after:shrink-0' // Add space before and after the page list
        : 'm-auto',
    ],
    viewMode === ViewMode.Grid ? 'flex-1 my-[80rem] lg:px-[20rem] sm:px-[10rem]' : ''
  );

  const itemClasses = classNames([
    'flex',
    'flex-col',
    'snap-center',
    ViewMode.List === viewMode ? 'mb-[40rem]' : '',
  ]);

  const tpxCoreAttrs = {
    [CoreDoms.ProjectViewerAttr]: Viewers.Primary,
    [CoreDoms.MaxWidthAttr]: '90',
    [CoreDoms.MaxHeightAttr]: '65',
    [CoreDoms.ResizeThresholdAttr]: '10',
  };

  useEffect(() => {
    // Switch the view mode to list when changing to small screen
    if (ui.state.isSmallScreen) {
      setViewMode(ViewMode.List);
    }
  }, [ui.state.isSmallScreen]);

  const gridGap = ui.state.isSmallScreen ? 15 : 20;

  if (!app.state.loaded) {
    return (
      <LoadingOverlay
        id={'app-loading-overlay'}
        open={true}
        theme={ThemeName.Container}
        overlayColour={'transparent'}
      />
    );
  }

  return (
    <Theme
      name={'tpx-theme-main'}
      allowBorder={false}
      allowCorners={false}
      tipLabel={'Base'}
      id="tpx-app"
      className={appClasses}
    >
      <main className="flex flex-1 max-w-full max-h-full min-h-0 sm:flex-col">
        <Theme
          name={'tpx-theme-workarea'}
          tipLabel={'Workarea'}
          isRootTheme
          allowBorder={false}
          id="workarea"
          data-track-position
          className={workAreaClassNames}
          allowCorners={false}
        >
          {/* <WorkareaToolbar layout={layout} /> */}

          {!ui.state.isSmallScreen && (
            <div id={'viewerToolbar'} className={viewerToolbarClasses}>
              <div id="centreButtons" className={centerClasses}>
                <Theme name={ThemeName.Popouts} className={gridSizeToolbarClasses}>
                  <Button
                    startIcon={<ListViewIcon />}
                    hideLabel={ui.state.isSmallScreen}
                    buttonStyle={'standard'}
                    tabPosition={'left'}
                    aria-pressed={viewMode === ViewMode.List}
                    onClick={() => onSetViewMode(ViewMode.List)}
                    size={'small'}
                  />
                  <Button
                    startIcon={<GridViewIcon />}
                    hideLabel={ui.state.isSmallScreen}
                    buttonStyle={'standard'}
                    corners={'square'}
                    aria-pressed={viewMode === ViewMode.Grid}
                    onClick={() => onSetViewMode(ViewMode.Grid)}
                    size={'small'}
                  />
                </Theme>
              </div>

              {viewMode === ViewMode.List && (
                <div id="rightSideButtons" className={rightSideClasses}>
                  <ZoomControl />
                </div>
              )}
            </div>
          )}
          <div id={'paginationWrapper'} className={paginationWrapperClasses}>
            <div
              {...tpxCoreAttrs}
              className={projectViewerClassNames}
              ref={containerRef}
              id={'projectViewer'}
            >
              <ul data-project-pages="true" className={pagesContainerClasses}>
                {pages.map((page: PageInterface) => {
                  return (
                    <li data-id={page.pagenumber} className={itemClasses} key={`share-${page.ref}`}>
                      <Preview
                        page={page}
                        layout={layout}
                        editorType={EditorExperienceType.ReadOnly}
                        viewMode={viewMode}
                        gridCellMaxWidth={GridSizes.LARGE}
                        gridRowGap={gridGap}
                        gridColGap={gridGap}
                      >
                        <PageName page={page} layout={layout} />
                      </Preview>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </Theme>
      </main>
    </Theme>
  );
};

export default SharePreview;
